/**
 * FilterBar — Flexible hierarchy filter bar.
 *
 * All dropdowns (BDM, ZM, HQ, Brand) become available as soon as Division
 * is selected. Selecting any filter narrows downstream options via frontend
 * caching — no extra API calls.
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { useFilters } from '../../context/FilterContext';
import './FilterBar.css';

// ── Dropdown with search ──────────────────────────────────────────────────────
const Dropdown = ({
  label,
  value,
  displayValue,
  options,
  onSelect,
  disabled,
  loading,
  count,          // total options count shown as badge
  placeholder,
  alwaysShowSearch = false,
}) => {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const ref     = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const handleOpen = () => {
    if (disabled || loading) return;
    setOpen(o => !o);
    setSearch('');
  };

  return (
    <div className="pd-dropdown" ref={ref}>
      <div className="pd-dropdown__label">
        {label}
        {count > 0 && !disabled && (
          <span className="pd-dropdown__count">{count}</span>
        )}
      </div>

      <div
        className={[
          'pd-dropdown__header',
          disabled ? 'pd-dropdown__header--disabled' : '',
          value    ? 'pd-dropdown__header--active'   : '',
        ].join(' ')}
        onClick={handleOpen}
        title={disabled ? 'Select a Division first' : ''}
      >
        <span className="pd-dropdown__value">
          {displayValue || placeholder || `All ${label}s`}
        </span>
        {loading
          ? <span className="pd-spinner" />
          : <span className={`pd-dropdown__arrow ${open ? 'pd-dropdown__arrow--open' : ''}`}>v</span>
        }
      </div>

      {open && (
        <div className="pd-dropdown__list">
          {/* Search box — show if more than 6 options or alwaysShowSearch */}
          {(alwaysShowSearch || options.length > 6) && (
            <div className="pd-dropdown__search-wrap">
              <input
                ref={inputRef}
                className="pd-dropdown__search"
                type="text"
                placeholder={`Search ${label}...`}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </div>
          )}

          {/* Clear / All option */}
          <div
            className={`pd-dropdown__item ${!value ? 'pd-dropdown__item--selected' : ''}`}
            onClick={() => { onSelect('', ''); setOpen(false); setSearch(''); }}
          >
            All {label}s
          </div>

          {filtered.length === 0 && (
            <div className="pd-dropdown__empty">No results</div>
          )}

          {filtered.map((opt, idx) => (
            <div
              key={opt.value || `opt-${idx}`}
              className={`pd-dropdown__item ${value === opt.value ? 'pd-dropdown__item--selected' : ''}`}
              onClick={() => { onSelect(opt.value, opt.label); setOpen(false); setSearch(''); }}
            >
              {opt.primary ? (
                <>
                  <div className="pd-dropdown__item-top">
                    <span className="pd-dropdown__item-name">{opt.primary}</span>
                    {opt.meta && <span className="pd-dropdown__item-meta">{opt.meta}</span>}
                  </div>
                  {opt.sub && (
                    <div className="pd-dropdown__item-bottom">
                      <span>{opt.sub}</span>
                      {opt.badge != null && (
                        <span className={`pd-badge ${opt.badge >= 100 ? 'pd-badge--green' : opt.badge >= 85 ? 'pd-badge--orange' : 'pd-badge--red'}`}>
                          {opt.badge}%
                        </span>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <span>{opt.label}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── FilterBar ─────────────────────────────────────────────────────────────────
const FilterBar = () => {
  const {
    filters, updateFilter, updateFilters, resetFilters, hasActiveFilters,
    fiscalYearOptions, fiscalYearsLoading,
    divisionOptions,   divisionsLoading,
    stateOptions,      stateLoading,
    monthOptions,
    bdmOptions,        bdmLoading,
    zmOptions,         zmLoading,
    hqOptions,         hqLoading,
    brandOptions,      brandLoading,
    hierarchyLoading,
  } = useFilters();

  const divisionSelected = !!filters.division;

  // ── Option arrays ─────────────────────────────────────────────────────────
  const fyOpts = fiscalYearOptions.map(f => ({
    value: f.id || f.value || f, label: f.label || f.id || f, primary: f.id || f.label || f,
  }));

  const monthOpts = monthOptions.map(m => ({
    value: m.id, label: m.label, primary: m.label,
  }));

  // `getDivisions` already normalized the wire shape (strings or objects) into
  // `{ value, label }`, so there is no branch left here — only the `primary` field the
  // dropdown renders (Req 7.2).
  const divOpts = divisionOptions.map(d => ({
    value: d.value, label: d.label, primary: d.label,
  }));

  const stateOpts = stateOptions.map(s => ({
    value: s.value, label: s.label, primary: s.primary,
  }));

  const bdmOpts = bdmOptions.map(b => ({
    value: b.value, label: b.label, primary: b.primary, meta: b.meta,
  }));

  const zmOpts = zmOptions.map(z => ({
    value: z.value, label: z.label, primary: z.primary,
  }));

  const hqOpts = hqOptions.map(h => ({
    value: h.value, label: h.label, primary: h.primary,
  }));

  const brandOpts = brandOptions.map(b => ({
    value: b.value, label: b.label, primary: b.primary, sub: b.sub,
  }));

  // Active filter count for the reset badge
  const activeCount = [filters.yearmonth, filters.state, filters.bdm, filters.zm, filters.hq, filters.brand]
    .filter(Boolean).length;

  return (
    <div className="pd-filterbar">

      {/* Hierarchy loading — animated bar at bottom, not a flex item */}
      {hierarchyLoading && <div className="pd-hierarchy-loading" />}
      <Dropdown
        label="Fiscal Year"
        value={filters.fiscalYear}
        displayValue={filters.fiscalYear}
        options={fyOpts}
        onSelect={(val, lbl) => {
          updateFilters({ fiscalYear: val, fiscalYearLabel: lbl });
        }}
        loading={fiscalYearsLoading}
        count={fyOpts.length}
      />

      {/* Month — optional, available once FY is selected */}
      <Dropdown
        label="Month"
        value={filters.yearmonth}
        displayValue={filters.yearmonthLabel || filters.yearmonth}
        options={monthOpts}
        onSelect={(val, lbl) => {
          updateFilters({ yearmonth: val, yearmonthLabel: lbl });
        }}
        disabled={!filters.fiscalYear}
        loading={fiscalYearsLoading}
        count={monthOpts.length}
        placeholder="All Months"
      />

      {/* Division */}
      <Dropdown
        label="Division"
        value={filters.division}
        displayValue={filters.division}
        options={divOpts}
        onSelect={(val) => updateFilter('division', val)}
        loading={divisionsLoading}
        count={divOpts.length}
        alwaysShowSearch
      />

      <Dropdown
        label="State"
        value={filters.state}
        displayValue={filters.state}
        options={stateOpts}
        onSelect={(val) => updateFilter('state', val)}
        loading={stateLoading}
        disabled={!divisionSelected}
        count={stateOpts.length}
        placeholder="All States"
      />

      {/* BDM — available as soon as Division is selected */}
      <Dropdown
        label="BDM"
        value={filters.bdm}
        displayValue={filters.bdmLabel || filters.bdm}
        options={bdmOpts}
        onSelect={(val, lbl) => {
          updateFilters({ bdm: val, bdmLabel: lbl });
        }}
        loading={bdmLoading}
        disabled={!divisionSelected}
        count={bdmOpts.length}
        placeholder="All BDMs"
      />

      {/* ZM — available as soon as Division is selected; narrows if BDM chosen */}
      <Dropdown
        label="ZM"
        value={filters.zm}
        displayValue={filters.zmLabel || filters.zm}
        options={zmOpts}
        onSelect={(val, lbl) => {
          updateFilters({ zm: val, zmLabel: lbl });
        }}
        loading={zmLoading}
        disabled={!divisionSelected}
        count={zmOpts.length}
        placeholder="All ZMs"
      />

      {/* MIS HQ — available as soon as Division is selected; narrows by BDM/ZM */}
      <Dropdown
        label="MIS HQ"
        value={filters.hq}
        displayValue={filters.hq}
        options={hqOpts}
        onSelect={(val) => updateFilter('hq', val)}
        loading={hqLoading}
        disabled={!divisionSelected}
        count={hqOpts.length}
        placeholder="All HQs"
        alwaysShowSearch
      />

      {/* Brand — available as soon as Division is selected; narrows by BDM/ZM/HQ */}
      <Dropdown
        label="Brand"
        value={filters.brand}
        displayValue={filters.brand}
        options={brandOpts}
        onSelect={(val) => updateFilter('brand', val)}
        loading={brandLoading}
        disabled={!divisionSelected}
        count={brandOpts.length}
        placeholder="All Brands"
      />

      {/* Reset */}
      <button
        className={`pd-reset ${hasActiveFilters ? 'pd-reset--on' : ''}`}
        onClick={resetFilters}
        disabled={!hasActiveFilters}
      >
        Reset{activeCount > 0 ? ` (${activeCount})` : ''}
      </button>

    </div>
  );
};

export default FilterBar;
